import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { MatchingColorsMap, TEXTRACT_MATCH_STATUSES } from '@/components/TwoWayMatch/matching';
import { twoWayMatchBindingButtonPosition } from '@/pages/document/DocumentViewer/DocumentFileView/PageView/PageTwoWayMatchLayer/twoWayMatchGeometry';
import { usePopupState, bindTrigger, bindMenu } from 'material-ui-popup-state/hooks';

import Menu from '@mui/material/Menu';
import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import Divider from '@mui/material/Divider';
import CircularProgress from '@mui/material/CircularProgress';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import type { FieldMapping } from '@/domain/textract/schemas';

import boundIcon from '@/pages/document/DocumentViewer/DocumentFileView/PageView/PageTwoWayMatchLayer/TwoWayMatchColumnBindings/assets/bound.svg';
import unboundIcon from '@/pages/document/DocumentViewer/DocumentFileView/PageView/PageTwoWayMatchLayer/TwoWayMatchColumnBindings/assets/unbound.svg';
import CheckIcon from '@mui/icons-material/Check';

type TwoWayMatchColumnBindingButtonProps = {
  bindingCell: unknown;
  name: unknown;
  fields: FieldMapping[];
  isMandatory: boolean;
  mandatoryField: boolean;
  bound: boolean;
  width: number;
  height: number;
  turnFromUpright: number;
  onClick: (name: string) => void;
  onSelect: (index: number, mandatory: boolean | undefined) => void;
  selectedFieldIndex: number | null;
  disabledFields: number[];
  isLoading: boolean;
  isPending: boolean;
};

export const TwoWayMatchColumnBindingButton = ({
  bindingCell,
  fields,
  isMandatory,
  width,
  height,
  turnFromUpright,
  onClick,
  name,
  bound,
  selectedFieldIndex,
  mandatoryField,
  disabledFields,
  onSelect,
  isLoading,
  isPending,
}: TwoWayMatchColumnBindingButtonProps) => {
  const theme = useTheme();
  const [mandatory, setMandatory] = useState(isMandatory);
  const toggleMandatory = useCallback(() => setMandatory((prevState) => !prevState), [setMandatory]);
  const popupState = usePopupState({ variant: 'popover', popupId: 'TextractFieldsDropdown' });

  const triggerProps = useMemo(() => {
    const menuProps = bindTrigger(popupState);

    return {
      ...menuProps,
      onClick: (event: React.MouseEvent) => {
        onClick(name);
        menuProps.onClick(event);
      },
    };
  }, [popupState, onClick, name]);

  useEffect(() => {
    setMandatory(mandatoryField);
  }, [mandatoryField]);

  const onMandatoryChecked = (_event: React.SyntheticEvent, checked: boolean) => {
    // if dropdown option selected, invoke parent component callback, otherwise change local checkbox state
    toggleMandatory();
    if (selectedFieldIndex !== null) {
      onSelect(selectedFieldIndex, checked);
    }
  };

  const disabledFieldsExceptSelected = useMemo(
    () => disabledFields.filter((field) => field !== selectedFieldIndex),
    [disabledFields, selectedFieldIndex],
  );

  const buttonStyle = useMemo((): React.CSSProperties => {
    if (!(bindingCell && bindingCell[0])) return {};
    const elementSize = 16;
    const binding = bound ? TEXTRACT_MATCH_STATUSES.MATCH : TEXTRACT_MATCH_STATUSES.PARTIAL_MATCH;
    const icon = bound ? boundIcon : unboundIcon;
    const [color, image] = isPending
      ? [theme.palette.action.disabled, 'none']
      : [MatchingColorsMap[binding].border, `url("${icon}")`];
    const { top, left } = twoWayMatchBindingButtonPosition(
      bindingCell,
      turnFromUpright,
      { width, height },
      elementSize,
    );

    return {
      position: 'absolute',
      top,
      left,
      width: `${elementSize}px`,
      height: `${elementSize}px`,
      border: 0,
      backgroundImage: image,
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
      backgroundColor: color,
      backgroundSize: elementSize / 2 - 1,
      zIndex: 100,
      cursor: isPending ? 'default' : 'pointer',
    };
  }, [bindingCell, bound, isPending, theme, turnFromUpright, width, height]);

  return bindingCell && bindingCell[0] ? (
    <>
      <button type="button" {...triggerProps} style={buttonStyle} disabled={isPending}>
        {isPending && (
          <CircularProgress size={10} thickness={6} sx={{ color: 'common.white', display: 'block', m: 'auto' }} />
        )}
      </button>
      <Menu
        {...bindMenu(popupState)}
        transitionDuration={0}
        slotProps={{ paper: { sx: { maxHeight: 700, width: 300 } }, list: { disablePadding: true } }}
      >
        <Box sx={{ position: 'sticky', top: 0, p: 2, pb: 1, bgcolor: 'background.paper', zIndex: 1 }}>
          <Typography variant="h6" sx={{ fontSize: 18 }}>
            <FormattedMessage id="3WayMatching.bindings.matchTo" defaultMessage="Matching to" />
          </Typography>
          <FormControlLabel
            checked={mandatory}
            onChange={onMandatoryChecked}
            control={<Switch size="small" />}
            label={<FormattedMessage id="3WayMatching.bindings.mandatory" defaultMessage="Mandatory field" />}
            labelPlacement="start"
            sx={{ ml: 0, color: 'text.secondary' }}
          />
          <Divider sx={{ mt: 1 }} />
        </Box>
        {isLoading && fields.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <List disablePadding>
            {fields.map((option, index) => (
              <ListItem
                key={option.name}
                disablePadding
                {...(index === selectedFieldIndex
                  ? {
                      secondaryAction: isLoading ? <CircularProgress size={16} /> : <CheckIcon color="success" />,
                    }
                  : {})}
              >
                <ListItemButton
                  key={option.displayName}
                  selected={index === selectedFieldIndex && !isLoading}
                  disabled={disabledFieldsExceptSelected.includes(index)}
                  onClick={() => onSelect(index, mandatory)}
                  dense
                >
                  {option.displayName}
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        )}
      </Menu>
    </>
  ) : null;
};
